import {ApiRef, createApiRef} from '@backstage/core-plugin-api';
import {Config} from '@backstage/config';

const repoExistsRequestOptions: RequestInit = {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
  },
};

export const bitbucketUtilitiesApi = (config: Config): UtilitiesApi => {
  const baseUrl = config.getString('backend.baseUrl');
  const repoExists = async (repo_slug: string) => {
    if (!repo_slug) {
      throw new Error('repo_slug is required');
    }

    try {
      const url = `${baseUrl}/api/bitbucket-utilities/repo_exists?${new URLSearchParams({
        repo_slug,
      })}`;
      const response = await fetch(url, repoExistsRequestOptions);
      if (response.status !== 200) {
        throw new Error(`${response.status} ${response.statusText}, ${await response.text()}`);
      }
      const data = (await response.json()) as {result: boolean; main_branch?: string};
      return {exists: data.result, mainBranch: data.main_branch};
    } catch (e) {
      throw new Error(`Unexpected response from validation, ${e}`);
    }
  };

  const inviteUser = async (repoSlug: string, userEmail: string) => {
    if (!repoSlug || !userEmail) {
      throw new Error('repoSlug and userEmail are required');
    }
    const inviteUserRequestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_email: userEmail,
        repo_slug: repoSlug,
      }),
    };
    try {
      const url = `${baseUrl}/api/bitbucket-utilities/invite_user`;

      const response = await fetch(url, inviteUserRequestOptions);
      if (response.status !== 200) {
        throw new Error(`${response.status} ${response.statusText}, ${await response.text()}`);
      }
      const data = (await response.json()) as {result: boolean};
      const success = data.result;
      return success;
    } catch (e) {
      return false;
    }
  };

  const doesUserHavePermission = async (repoSlug: string, userEmail: string) => {
    if (!repoSlug || !userEmail) {
      throw new Error('repoSlug and userEmail are required');
    }
    const doesUserHavePermissionRequestOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const params = {
      repo_slug: repoSlug,
      user_email: userEmail,
    };
    try {
      const url = `${baseUrl}/api/bitbucket-utilities/get_permissions_for_user?${new URLSearchParams(
        params,
      )}`;
      const response = await fetch(url, doesUserHavePermissionRequestOptions);
      if (response.status !== 200) {
        throw new Error(`${response.status} ${response.statusText}, ${await response.text()}`);
      }
      const data = (await response.json()) as {result: boolean};
      const hasPermission = data.result;
      return hasPermission;
    } catch (e) {
      return false;
    }
  };
  const createCatalogInfoPr = async (repoSlug: string, yaml: string, registeredBy: string) => {
    if (!repoSlug || !yaml) {
      throw new Error('repoSlug and yaml are required');
    }
    const createCatalogInfoPrRequestOptions: RequestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        repo_slug: repoSlug,
        yaml,
        registered_by: registeredBy,
      }),
    };
    const url = `${baseUrl}/api/bitbucket-utilities/create_catalog_info_pr`;
    const response = await fetch(url, createCatalogInfoPrRequestOptions);
    if (response.status !== 200) {
      throw new Error(`${response.status} ${response.statusText}, ${await response.text()}`);
    }
    const data = (await response.json()) as {result: {id: number; url: string; branch: string}};
    return data.result;
  };

  return {repoExists, inviteUser, doesUserHavePermission, createCatalogInfoPr};
};

export type RepoExistsResult = {
  exists: boolean;
  mainBranch?: string;
};

export type UtilitiesApi = {
  repoExists(repo_slug: string, baseUrl: string): Promise<RepoExistsResult>;
  inviteUser(repoSlug: string, userEmail: string): Promise<boolean>;
  doesUserHavePermission(repoSlug: string, userEmail: string): Promise<boolean>;
  createCatalogInfoPr(
    repoSlug: string,
    yaml: string,
    registeredBy: string,
  ): Promise<{id: number; url: string; branch: string}>;
};

export const utilitiesApiRef: ApiRef<UtilitiesApi> = createApiRef({
  id: 'utilitiesapi.custom',
});
